import { useEffect, useMemo, useState } from 'react';

import { apiFetch, ApiRequestError } from '@/lib/api/client';
import { buildProbePath, loadProbes, probeQueryPreview, type Probe, type ProbeDraft } from '@/pages/aiagent/probes';

/**
 * 프로브 카드 — <b>눌러서 조회해 보는 칸</b> (DOC-109 §4).
 *
 * <h2>왜 확인 게이트가 없나</h2>
 * 쓰기 초안(`ConfirmRow`)은 「저장할까요」를 묻는다. 여기는 묻지 않는다 — <b>되돌릴 것이 없기
 * 때문이다.</b> 조회는 그 사람이 이미 볼 수 있는 자료를 한 번 더 보는 일이고, 권한 밖이면
 * 눌러도 403 이 돌아온다. 되돌릴 수 없는 일에만 확인을 둔다.
 *
 * <h2>왜 값을 사람이 고칠 수 있나</h2>
 * 에이전트가 채운 값은 <b>초안</b>이다. 「API 어떻게 돼?」 하고 묻는 사람이 보고 싶은 것은 한 번의
 * 답이 아니라 <b>값을 바꿔 가며 부르는 모양</b>이다 — 스웨거의 "Try it out" 이 하던 일이고,
 * 그 자리를 대화 안으로 옮긴 것이 이 카드다.
 *
 * <h2>안 되는 것</h2>
 * <ul>
 *   <li><b>GET 뿐이다.</b> 카탈로그에 쓰기 키가 없다 — 버튼이 쓰기를 부를 방법이 구조적으로 없다.</li>
 *   <li><b>경로를 받지 않는다.</b> 에이전트가 주는 것은 키이고, 경로는 서버 카탈로그가 안다.</li>
 *   <li><b>모르는 키는 카드를 세우지 않는다.</b> 조용히 넘기지 않고 그렇게 말한다 — 요청이
 *       증발한 것처럼 보이면 사용자는 자기가 잘못 말한 줄 안다(`action` 과 같은 규율).</li>
 * </ul>
 *
 * <h2>기록</h2>
 * 호출에 {@code X-Probe-Key} 를 붙인다. 그것 때문에 더 열리는 것은 없고, 하는 일은
 * {@code sys_activity_logs} 에 「누가 무엇을 눌러 봤나」를 남기는 것뿐이다 —
 * 조회는 원래 남지 않으므로 이 통로만 따로 적는다(`ActivityLogWriter.logProbe`).
 */

type Outcome =
  | { kind: 'ok'; status: number; ms: number; count: number | null; body: string }
  | { kind: 'error'; status: number; ms: number; message: string; body: string };

export function ProbeCard({ draft, compact = false }: { draft: ProbeDraft; compact?: boolean }) {
  const [probe, setProbe] = useState<Probe | null>(null);
  const [unknown, setUnknown] = useState(false);
  const [values, setValues] = useState<Record<string, string>>(draft.values ?? {});
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    let alive = true;
    loadProbes()
      .then((list) => {
        if (!alive) return;
        const found = list.find((p) => p.key === draft.key) ?? null;
        setProbe(found);
        setUnknown(!found);
      })
      .catch(() => alive && setUnknown(true));
    return () => {
      alive = false;
    };
  }, [draft.key]);

  const preview = useMemo(() => (probe ? probeQueryPreview(probe, values) : ''), [probe, values]);

  if (unknown) {
    return (
      <div className="card p-4">
        <p style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
          이 조회는 지금 열려 있지 않습니다. 화면에서 직접 확인해 주세요.
        </p>
      </div>
    );
  }

  if (!probe) return null;

  const run = async () => {
    setRunning(true);
    const path = buildProbePath(probe, values);
    const started = performance.now();
    try {
      const data = await apiFetch<unknown>(path, { headers: { 'X-Probe-Key': probe.key } });
      setOutcome({
        kind: 'ok',
        status: 200,
        ms: Math.round(performance.now() - started),
        count: Array.isArray(data) ? data.length : null,
        // 봉투를 다시 씌워 보여 준다 — 사람이 실제로 받는 모양이 `{ok, data}` 이기 때문이다.
        body: JSON.stringify({ ok: true, data }, null, 2),
      });
    }
    catch (error) {
      const api = error instanceof ApiRequestError ? error : null;
      setOutcome({
        kind: 'error',
        status: api?.status ?? 0,
        ms: Math.round(performance.now() - started),
        // 사유를 우리가 지어내지 않는다 — 서버가 준 문장을 그대로 옮긴다.
        message: api?.apiError?.message ?? '요청을 보내지 못했습니다.',
        body: JSON.stringify(
          { ok: false, error: api?.apiError ?? { code: 'NETWORK', message: '요청을 보내지 못했습니다.' } },
          null,
          2,
        ),
      });
    }
    finally {
      setRunning(false);
    }
  };

  const pre = compact ? '10px' : '11px';

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{probe.name}</p>
        <span
          className="rounded-md px-2 py-0.5 font-mono"
          style={{ fontSize: '11px', background: 'var(--accent-bg)', color: 'var(--accent)' }}
        >
          {probe.method} {probe.path}
        </span>
      </div>

      {probe.params.length > 0 && (
        <div className="space-y-1.5">
          {probe.params.map((param) => (
            <div key={param.name} className="flex items-center gap-2">
              <span
                className="font-mono text-right truncate"
                style={{ fontSize: '11px', color: 'var(--text-muted)', width: 64, flexShrink: 0 }}
              >
                {param.name}
              </span>
              <input
                className="input"
                value={values[param.name] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [param.name]: e.target.value }))}
                style={{ fontSize: '12px', padding: '4px 8px', width: 160 }}
              />
              <span className="truncate" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                {param.hint}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={run}
          disabled={running}
          className="btn-primary disabled:opacity-60"
          style={{ fontSize: '12px', padding: '5px 14px' }}
        >
          {running ? '조회 중…' : '조회'}
        </button>
        {preview && (
          <span className="font-mono truncate" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {preview}
          </span>
        )}
      </div>

      {outcome && (
        <div className="space-y-2" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <div className="flex items-center gap-2">
            <span
              className="rounded px-1.5 py-0.5 font-mono"
              style={
                outcome.kind === 'ok'
                  ? { fontSize: '11px', background: '#dcfce7', color: '#166534' }
                  : { fontSize: '11px', background: '#fee2e2', color: '#991b1b' }
              }
            >
              {outcome.status || '—'} {outcome.kind === 'ok' ? 'OK' : '실패'}
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {outcome.ms}ms{outcome.kind === 'ok' && outcome.count !== null ? ` · ${outcome.count}건` : ''}
            </span>
          </div>

          {outcome.kind === 'error' && (
            <p style={{ fontSize: '12px', color: 'var(--text-primary)', lineHeight: 1.7 }}>{outcome.message}</p>
          )}

          <pre
            className="overflow-x-auto rounded-lg p-3 font-mono"
            style={{
              fontSize: pre,
              lineHeight: 1.6,
              background: 'var(--bg)',
              color: 'var(--text-primary)',
              margin: 0,
              maxHeight: 280,
            }}
          >
            {outcome.body}
          </pre>
        </div>
      )}
    </div>
  );
}
